import { BuildingEntryType } from "../../../Types/buildingTypes"
import { useState } from "react"
import { Form, Formik } from "formik"
import { TextInputField } from "../../FormFields/TextInput/TextInputField"
import ImageUploadField from "../../FormFields/ImageUpload/ImageUpload"
import { modImageName } from "../../FormFields/ImageUpload/imageUploadHelpers"
import Collapsible from "../../Collapsible/Collapsible";
import { ArrayFieldInput } from "../../FormFields/ArrayInput/ArrayFieldInput"
import { CheckBoxInputField } from "../../FormFields/CheckBoxInput/CheckBoxInputField"
import { Button, Row, Col } from "react-bootstrap"
import { imageGalleryFieldValues, linkedServicesFieldValues } from "../commonFieldValues"
import AddressInputField from "../../FormFields/AddressInput/AddressInputField"
import { blankBuilding, buildingSchema } from "./BuildingFormHelpers"
import { FindCoordinatesModal } from "../../Modals/FindCoordinatesModal"
import { SelectBuildingModal } from "../../Modals/SelectBuildingModal/selectBuildingModal"
import { SelectPropertyModal } from "../../Modals/SelectPropertyModal/selectPropertyModal"
import merge from 'deepmerge'
import { SelectInputField } from "../../FormFields/SelectInput/SelectInputField"
import { checkVal } from "../../../Helpers/Parsers/checkVal"
import {
  aboutSectionsFieldValues,
  extraFeesFieldValues,
  generateBlankAboutFieldEntry,
  hotelCheckInFee,
  hotelCleaningFee,
  hotelDepositFee,
  hotelResortFee,
  hourSelection,
  minuteSelection,
} from "../PropertyForm/PropertyFormConstants"

const buildingDescriptionSections = [
  { sectionId: 'the-space', sectionTitle: 'The Space' },
  { sectionId: 'included-with-stay', sectionTitle: 'Included with Stay' },
  { sectionId: 'hospitality', sectionTitle: 'Hospitality' },
  { sectionId: 'arrival-checkin', sectionTitle: 'Arrival & Check-In' },
  { sectionId: 'fees-policies', sectionTitle: 'Fees & Policies' },
  { sectionId: 'additional-cleaning', sectionTitle: 'Additional Cleaning' },
  { sectionId: 'towel-linen-supply', sectionTitle: 'Towel & Linen Supply' },
  { sectionId: 'dining', sectionTitle: 'Dining' },
  { sectionId: 'maintenance', sectionTitle: 'Maintenance' },
  { sectionId: 'about-building', sectionTitle: 'About the Building' },
  { sectionId: 'important-notes', sectionTitle: 'Important Notes' },
]

const buildingGuestInstructions = [
  { sectionId: 'general', sectionTitle: 'General', content: '' },
  { sectionId: 'amenities', sectionTitle: 'Amenities', content: '' },
  { sectionId: 'additionalFees', sectionTitle: 'Additional Fees', content: '' },
  { sectionId: 'lateCheckInFee', sectionTitle: 'Late Check In Fee', content: 'After 10PM an extra $50 fee\nAfter 12AM an extra $100 fee' },
  { sectionId: 'lock', sectionTitle: 'Lock', content: '' },
  { sectionId: 'internet', sectionTitle: 'Internet', content: 'WIFI NAME: XXXXXXXX\nWIFI PASSWORD: ZZZZZZZZ' },
  { sectionId: 'parking', sectionTitle: 'Parking', content: '' },
]

const buildingGuestCheckOutInstructions = [
  { sectionId: 'trash', sectionTitle: 'Throw trash away', content: 'Please place trash in outside bins.' },
  { sectionId: 'turnThingsOff', sectionTitle: 'Turn Things Off', content: 'Please turn off all lights and set thermostat to 74 degrees.' },
  { sectionId: 'lockUp', sectionTitle: 'Lock Up', content: 'Close the door behind you.' },
  { sectionId: 'returnKeys', sectionTitle: 'Return Keys', content: 'Return key cards to front desk' },
  { sectionId: 'thermoStat', sectionTitle: 'Thermo Stat', content: 'Set thermostat to 74 degrees.' },
  { sectionId: 'additionalRequests', sectionTitle: 'Additional Requests', content: '' },
]

type CopySettingsSource = 'building' | 'property'

const CopySettingsButtons = (props: {
  fieldName: string,
  onCopy: (source: CopySettingsSource, fieldName: string) => void,
  buildingLabel?: string,
  propertyLabel?: string,
}) => (
  <>
    <Button
      type="button"
      className="me-2 mb-2"
      variant="dark"
      size="sm"
      onClick={() => props.onCopy('building', props.fieldName)}>
      {props.buildingLabel || 'Copy Other Building'}
    </Button>
    <Button
      type="button"
      className="me-2 mb-2"
      variant="dark"
      size="sm"
      onClick={() => props.onCopy('property', props.fieldName)}>
      {props.propertyLabel || 'Copy Other Property'}
    </Button>
  </>
)

export default function BuildingForm(props: {
  disabled: boolean,
  onSubmit: (v: any) => Promise<any>,
  initialValues?: Partial<BuildingEntryType>,
  uploadImageFn: (v: any) => Promise<string>,
  uploadImageMultiFn: (v: { imageToUpload: any, filename: string }[]) => Promise<string[]>,
  imageUploadProgress: string,
}) {
  const {
    disabled,
    onSubmit,
    initialValues,
    uploadImageFn,
    uploadImageMultiFn,
    imageUploadProgress,
  } = props

  const [uploadingImage, setUploadingImage] = useState(false)
  const [findCoordinatesModalOpen, setFindCoordinatesModalOpen] = useState(false)
  const [copySettingsPath, setCopySettingsPath] = useState('')
  const [copySettingsSource, setCopySettingsSource] = useState<CopySettingsSource | null>(null)

  return (
    <div className="building-form">
      <Formik
        initialValues={merge(
          blankBuilding(),
          (initialValues ? initialValues : {})
        )}
        validationSchema={buildingSchema}
        // validate={values => { const errors: any = {}; return errors; }}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            const submissionResponse = await onSubmit(values)
            if (!submissionResponse.error) {
              return
            }
          } catch (err) {
            // custom error handler
          }
          setSubmitting(false)
        }}
      >
        {({ values, errors, touched, setFieldValue, isSubmitting }) => (
          <>
            <Form>

              <h3>General Info</h3>
              <TextInputField
                fieldLabel="Title"
                placeholder="title"
                required={true}
                fieldName="title" />

              <TextInputField
                fieldLabel="Description"
                placeholder="Description"
                required={true}
                fieldType={'textarea'}
                fieldName="description" />

              <h3>Buiding Address</h3>
              <AddressInputField
                fieldName={'address'}
                required={true}
              />

              <h3>🧭 Coordinates</h3>
              <CheckBoxInputField
                fieldLabel={'Hide exact coordinates from guests'}
                fieldName={'hideExactCoordinates'} />
              <Row>
                <Col xs={12} md={5}>
                  <TextInputField
                    fieldLabel="Longitude"
                    placeholder="Longitude"
                    subText="This value should be between -180 and 180"
                    fieldName={`coordinates[0]`} />
                </Col>
                <Col xs={12} md={5}>
                  <TextInputField
                    fieldLabel="Latitude"
                    placeholder="Latitude"
                    subText="This value should be between -90 and 90"
                    fieldName={`coordinates[1]`} />
                </Col>
                <Col xs={12} md={2}>
                  {/* center in box */}
                  <div className="d-flex justify-content-center">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="mb-3"
                      onClick={() => setFindCoordinatesModalOpen(true)}
                    >
                      🗺️ Find Coordinates
                    </Button>
                  </div>
                </Col>
              </Row>


              <h3>Featured Image</h3>
              {values.featuredImage && (
                <>
                  <img alt={'featured preview'} src={values.featuredImage} className="image-preview" />
                  <br />
                </>
              )}
              <TextInputField
                fieldLabel="Featured Image"
                placeholder="Featured Image"
                fieldName={`featuredImage`} />

              <TextInputField
                fieldLabel="Thumbnail Image"
                placeholder="Thumbnail Image"
                fieldName={`thumbnailImage`} />

              <ImageUploadField
                key={'featuredImageUpload'}
                uploadedImageCallback={async (imageInfo: any, thumbInfo: any) => {
                  setUploadingImage(true)
                  try {
                    const uploadedImageUrl = await uploadImageFn(imageInfo)
                    const uploadedThumbUrl = await uploadImageFn({
                      ...thumbInfo,
                      filename: modImageName(thumbInfo.filename, '_thumb')
                    })
                    if (uploadedImageUrl) {
                      setFieldValue('featuredImage', uploadedImageUrl)
                      setFieldValue('thumbnailImage', uploadedThumbUrl)
                    }
                  } catch (err) {
                    // something went wrong
                  }
                  setUploadingImage(false)
                }}
              />
              <br />

              <h3>Building Images</h3>
              <Collapsible>
                <div>
                  <label htmlFor="Image Gallery">Image Gallery</label><br />
                  <ArrayFieldInput
                    fieldName={'imageGallery'}
                    values={values.imageGallery}
                    setFieldValue={setFieldValue}
                    blankEntryFn={() => ({ url: '', title: '', description: '', thumbUrl: '' })}
                    imgPreview={{
                      srcVal: 'url'
                    }}
                    fields={imageGalleryFieldValues} />
                  {imageUploadProgress && (<>{imageUploadProgress} <br /></>)}
                  <ImageUploadField
                    key={'imageGalleryUpload'}
                    multipleImages={true}
                    uploadedImageCallback={async (imagesInfo: any[], thumbsInfo: any[]) => {
                      setUploadingImage(true)
                      try {
                        const uploadedImageList = await uploadImageMultiFn(imagesInfo)
                        const uploadedThumbs = await uploadImageMultiFn(thumbsInfo.map(x => {
                          return {
                            ...x,
                            filename: modImageName(x.filename, '_thumb')
                          }
                        }))
                        if (uploadedImageList) {
                          const mappedUploadedImages = uploadedImageList.map((imgUrl: string, idx: number) => {
                            const thumbUrl = uploadedThumbs && uploadedThumbs[idx] ? uploadedThumbs[idx] : ''
                            return {
                              url: imgUrl,
                              title: '',
                              description: '',
                              thumbUrl
                            }
                          })
                          setFieldValue('imageGallery', [...values.imageGallery, ...mappedUploadedImages])
                        }
                      } catch (err) {
                        // something went wrong
                      }
                      setUploadingImage(false)
                    }}
                  />
                </div>
              </Collapsible>

              <CopySettingsButtons
                fieldName="buildingDetails"
                buildingLabel="⚠️ Import From Other Building"
                propertyLabel="⚠️ Import From Other Property"
                onCopy={(source, fieldName) => {
                  setCopySettingsPath(fieldName)
                  setCopySettingsSource(source)
                }} />
              <h3>📇 Building Details</h3>
              <TextInputField
                fieldLabel="Summary"
                placeholder="Summary"
                fieldType="textarea"
                fieldName="about.summary" />

              <Collapsible title="ℹ️ Description Sections" TitleTag="h3">
                <div>
                  <label htmlFor="Building Description Sections">Description Sections</label><br />
                  {buildingDescriptionSections.map((section, idx) => {
                    if (values.about.sections.some((entry: any) => entry.sectionId === section.sectionId)) {
                      return null
                    }
                    return (
                      <Button
                        key={`add-building-section-${idx}`}
                        type="button"
                        className="me-2 mb-2"
                        variant="secondary"
                        size="sm"
                        onClick={() => setFieldValue('about.sections', [
                          ...values.about.sections,
                          { ...generateBlankAboutFieldEntry(), ...section }
                        ])}>
                        + {section.sectionTitle}
                      </Button>
                    )
                  })}
                  {values.about.sections.length === 0 && (
                    <CopySettingsButtons
                      fieldName="about.sections"
                      onCopy={(source, fieldName) => {
                        setCopySettingsPath(fieldName)
                        setCopySettingsSource(source)
                      }} />
                  )}
                  <ArrayFieldInput
                    fieldName="about.sections"
                    values={values.about.sections}
                    setFieldValue={setFieldValue}
                    blankEntryFn={generateBlankAboutFieldEntry}
                    uploadImageFn={uploadImageFn}
                    imgPreview={{ srcVal: 'sectionImage' }}
                    fields={aboutSectionsFieldValues} />
                </div>
              </Collapsible>

              <Collapsible title="🙋 Guest Instructions" TitleTag="h3">
                <div>
                  <label htmlFor="Building Guest Instructions">Guest Instructions</label><br />
                  {buildingGuestInstructions.map((instruction, idx) => (
                    <Button
                      key={`add-building-guest-instruction-${idx}`}
                      type="button"
                      className="me-2 mb-2"
                      variant="secondary"
                      size="sm"
                      onClick={() => setFieldValue('about.guestInstructions', [
                        ...values.about.guestInstructions,
                        instruction
                      ])}>
                      + {instruction.sectionTitle}
                    </Button>
                  ))}
                  {values.about.guestInstructions.length === 0 && (
                    <CopySettingsButtons
                      fieldName="about.guestInstructions"
                      onCopy={(source, fieldName) => {
                        setCopySettingsPath(fieldName)
                        setCopySettingsSource(source)
                      }} />
                  )}
                  <ArrayFieldInput
                    fieldName="about.guestInstructions"
                    values={values.about.guestInstructions}
                    setFieldValue={setFieldValue}
                    blankEntryFn={generateBlankAboutFieldEntry}
                    uploadImageFn={uploadImageFn}
                    imgPreview={{ srcVal: 'sectionImage' }}
                    fields={aboutSectionsFieldValues} />
                </div>
              </Collapsible>

              <Collapsible title="🚪 Check Out Instructions" TitleTag="h3">
                <div>
                  <label htmlFor="Building Check Out Instructions">Check Out Instructions</label><br />
                  {buildingGuestCheckOutInstructions.map((instruction, idx) => (
                    <Button
                      key={`add-building-checkout-instruction-${idx}`}
                      type="button"
                      className="me-2 mb-2"
                      variant="secondary"
                      size="sm"
                      onClick={() => setFieldValue('about.guestCheckOutInstructions', [
                        ...values.about.guestCheckOutInstructions,
                        instruction
                      ])}>
                      + {instruction.sectionTitle}
                    </Button>
                  ))}
                  {values.about.guestCheckOutInstructions.length === 0 && (
                    <CopySettingsButtons
                      fieldName="about.guestCheckOutInstructions"
                      onCopy={(source, fieldName) => {
                        setCopySettingsPath(fieldName)
                        setCopySettingsSource(source)
                      }}
                    />
                  )}
                  <ArrayFieldInput
                    fieldName="about.guestCheckOutInstructions"
                    values={values.about.guestCheckOutInstructions}
                    setFieldValue={setFieldValue}
                    blankEntryFn={generateBlankAboutFieldEntry}
                    uploadImageFn={uploadImageFn}
                    imgPreview={{ srcVal: 'sectionImage' }}
                    fields={aboutSectionsFieldValues} />
                </div>
              </Collapsible>

              <TextInputField
                fieldType="textarea"
                fieldLabel="📝 Registration Message"
                placeholder="Registration Message"
                fieldName="about.registrationMessage" />

              <CopySettingsButtons
                fieldName="about.registrationMessage"
                onCopy={(source, fieldName) => {
                  setCopySettingsPath(fieldName)
                  setCopySettingsSource(source)
                }}
              />

              <Row className="mb-2">
                <Col>
                  <h3>🕓 Check In / Check Out</h3>
                </Col>
                <Col xs="auto">
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => {
                      setFieldValue('about.checkInHour', 16)
                      setFieldValue('about.checkInMinute', 0)
                      setFieldValue('about.checkOutHour', 11)
                      setFieldValue('about.checkOutMinute', 0)
                    }}>
                    Use Standard Times
                  </Button>
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6}>
                  <SelectInputField
                    fieldLabel="Check In Hour"
                    fieldName="about.checkInHour"
                    required={true}
                    placeholder="- Select Hour -"
                    options={hourSelection} />
                </Col>
                <Col xs={12} md={6}>
                  <SelectInputField
                    fieldLabel="Check In Minute"
                    fieldName="about.checkInMinute"
                    required={true}
                    placeholder="- Select Minute -"
                    options={minuteSelection} />
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6}>
                  <SelectInputField
                    fieldLabel="Check Out Hour"
                    fieldName="about.checkOutHour"
                    required={true}
                    placeholder="- Select Hour -"
                    options={hourSelection} />
                </Col>
                <Col xs={12} md={6}>
                  <SelectInputField
                    fieldLabel="Check Out Minute"
                    fieldName="about.checkOutMinute"
                    required={true}
                    placeholder="- Select Minute -"
                    options={minuteSelection} />
                </Col>
              </Row>

              <h3>🏷️ Pricing</h3>
              <CopySettingsButtons
                fieldName="pricing"
                onCopy={(source, fieldName) => {
                  setCopySettingsPath(fieldName)
                  setCopySettingsSource(source)
                }} />
              <Row>
                <Col xs={12} md={3}>
                  <TextInputField
                    fieldType="number"
                    fieldLabel="Base Rate"
                    required={true}
                    placeholder="Base Rate"
                    fieldName="pricing.baseRate" />
                </Col>
                <Col xs={12} md={3}>
                  <TextInputField
                    fieldType="number"
                    fieldLabel="Deposit"
                    required={true}
                    placeholder="Deposit"
                    fieldName="pricing.deposit" />
                </Col>
                <Col xs={12} md={3}>
                  <TextInputField
                    fieldType="number"
                    fieldLabel="Tax %"
                    required={true}
                    placeholder="Tax %"
                    fieldName="pricing.tax" />
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={3}>
                  <TextInputField
                    fieldType="number"
                    fieldLabel="Cleaning"
                    required={true}
                    placeholder="Cleaning"
                    fieldName="pricing.cleaning" />
                </Col>
                <Col xs={12} md={3}>
                  <TextInputField
                    fieldType="number"
                    fieldLabel="Basic Cleaning"
                    required={true}
                    placeholder="Basic Cleaning"
                    fieldName="pricing.basicCleaning" />
                </Col>
              </Row>

              <Collapsible title="💸 Extra Fees" TitleTag="h3">
                <div>
                  <label htmlFor="Building Extra Fees">Extra Fees</label><br />
                  {values.pricing.extraFees.length === 0 && (
                    <CopySettingsButtons
                      fieldName="pricing.extraFees"
                      onCopy={(source, fieldName) => {
                        setCopySettingsPath(fieldName)
                        setCopySettingsSource(source)
                      }} />
                  )}
                  <Button
                    type="button"
                    className="me-2 mb-2"
                    variant="secondary"
                    size="sm"
                    onClick={() => setFieldValue('pricing.extraFees', [
                      ...values.pricing.extraFees,
                      hotelCleaningFee()
                    ])}>
                    + Hotel Cleaning
                  </Button>
                  <Button
                    type="button"
                    className="me-2 mb-2"
                    variant="secondary"
                    size="sm"
                    onClick={() => setFieldValue('pricing.extraFees', [
                      ...values.pricing.extraFees,
                      hotelDepositFee()
                    ])}>
                    + Hotel Deposit
                  </Button>
                  <Button
                    type="button"
                    className="me-2 mb-2"
                    variant="secondary"
                    size="sm"
                    onClick={() => setFieldValue('pricing.extraFees', [
                      ...values.pricing.extraFees,
                      hotelResortFee()
                    ])}>
                    + Hotel Resort
                  </Button>
                  <Button
                    type="button"
                    className="me-2 mb-2"
                    variant="secondary"
                    size="sm"
                    onClick={() => setFieldValue('pricing.extraFees', [
                      ...values.pricing.extraFees,
                      hotelCheckInFee()
                    ])}>
                    + Hotel Check-In
                  </Button>
                  <ArrayFieldInput
                    fieldName="pricing.extraFees"
                    values={values.pricing.extraFees}
                    setFieldValue={setFieldValue}
                    blankEntryFn={() => ({ feeId: '', amount: 0, frequency: 'once', title: '', description: '' })}
                    fields={extraFeesFieldValues} />
                </div>
              </Collapsible>

              <Collapsible
                title="🤖 Linked Services"
                TitleTag="h3"
              >
                <div>
                  <label htmlFor="Authorized Users">Linked Services</label><br />
                  <CopySettingsButtons
                    fieldName="linkedServices"
                    onCopy={(source, fieldName) => {
                      setCopySettingsPath(fieldName)
                      setCopySettingsSource(source)
                    }}
                  />
                  <ArrayFieldInput
                    fieldName={'linkedServices'}
                    values={values.linkedServices}
                    setFieldValue={setFieldValue}
                    blankEntryFn={() => ({ linkedService: '', linkedServiceId: '' })}
                    fields={linkedServicesFieldValues} />
                </div>
              </Collapsible>

              <h3>Public Building</h3>
              <CheckBoxInputField
                fieldLabel={'Display this building to the public'}
                fieldName={'public'} />

              Errors: {JSON.stringify(errors)}
              <br />
              <Button className="mb-3" type="submit" disabled={Object.keys(errors).length > 0 || isSubmitting || disabled || uploadingImage}>
                Submit
              </Button>
            </Form>
            <SelectBuildingModal
              active={copySettingsSource === 'building'}
              message={copySettingsPath === 'buildingDetails' ? '⚠️ This will copy and override all settings from the selected building' : 'Copy settings from another building'}
              onClose={() => {
                setCopySettingsSource(null)
                setCopySettingsPath('')
              }}
              filterFn={(building) => building._id !== initialValues?._id}
              onSelect={(selectedBuilding: any) => {
                if (copySettingsPath === 'buildingDetails') {
                  const defaults = blankBuilding()
                  const sourceAbout = selectedBuilding.about || {}
                  const sourcePricing = selectedBuilding.pricing || {}
                  setFieldValue('about', {
                    ...defaults.about,
                    ...sourceAbout,
                  })
                  setFieldValue('pricing', {
                    ...defaults.pricing,
                    ...sourcePricing,
                  })
                  if (selectedBuilding.linkedServices !== undefined) {
                    setFieldValue('linkedServices', selectedBuilding.linkedServices)
                  }
                  if (selectedBuilding.public !== undefined) {
                    setFieldValue('public', selectedBuilding.public)
                  }
                  return
                }
                const copiedSettings = checkVal(selectedBuilding, copySettingsPath)
                if (copiedSettings !== false) {
                  setFieldValue(copySettingsPath, copiedSettings)
                }
              }} />
            <SelectPropertyModal
              active={copySettingsSource === 'property'}
              message={copySettingsPath === 'buildingDetails' ? '⚠️ This will copy and override all settings from the selected property' : 'Copy settings from another property'}
              onClose={() => {
                setCopySettingsSource(null)
                setCopySettingsPath('')
              }}
              onSelect={(selectedProperty: any) => {
                if (copySettingsPath === 'buildingDetails') {
                  const defaults = blankBuilding()
                  const sourceAbout = selectedProperty.about || {}
                  const sourcePricing = selectedProperty.pricing || {}
                  setFieldValue('about', {
                    ...defaults.about,
                    summary: sourceAbout.summary ?? defaults.about.summary,
                    sections: sourceAbout.sections ?? defaults.about.sections,
                    checkInHour: sourceAbout.checkInHour ?? defaults.about.checkInHour,
                    checkInMinute: sourceAbout.checkInMinute ?? defaults.about.checkInMinute,
                    checkOutHour: sourceAbout.checkOutHour ?? defaults.about.checkOutHour,
                    checkOutMinute: sourceAbout.checkOutMinute ?? defaults.about.checkOutMinute,
                    registrationMessage: sourceAbout.registrationMessage ?? defaults.about.registrationMessage,
                    guestInstructions: sourceAbout.guestInstructions ?? defaults.about.guestInstructions,
                    guestCheckOutInstructions: sourceAbout.guestCheckOutInstructions ?? defaults.about.guestCheckOutInstructions,
                  })
                  setFieldValue('pricing', {
                    ...defaults.pricing,
                    ...sourcePricing,
                  })
                  if (selectedProperty.linkedServices !== undefined) {
                    setFieldValue('linkedServices', selectedProperty.linkedServices)
                  }
                  if (selectedProperty.public !== undefined) {
                    setFieldValue('public', selectedProperty.public)
                  }
                  return
                }
                const copiedSettings = checkVal(selectedProperty, copySettingsPath)
                if (copiedSettings !== false) {
                  setFieldValue(copySettingsPath, copiedSettings)
                }
              }} />
            <FindCoordinatesModal
              active={findCoordinatesModalOpen}
              onClose={() => setFindCoordinatesModalOpen(false)}
              address={values.address}
              onCoordinatesFound={(coordinates) => {
                setFieldValue('coordinates', coordinates)
                setFindCoordinatesModalOpen(false)
              }}
            />
          </>
        )}
      </Formik>
    </div>
  )
}
